/** @jsxImportSource react */
import { Alert, Button, Chip, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DynamicFormOutlinedIcon from '@mui/icons-material/DynamicFormOutlined'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { formsAPI } from '../../api/crm'
import { useTenantKey } from '../../hooks/useCrmQueries'
import { entityLabel, formatRelativeTime } from '../../utils/helpers'
import { getErrorMessage } from '../../utils/apiErrors'
import PageHeader from '../../components/common/PageHeader'
import Loading from '../../components/common/Loading'
import EmptyState from '../../components/common/EmptyState'

const STATUS_COLOR = { DRAFT: 'warning', PUBLISHED: 'success', ARCHIVED: 'default' }

/** All versions come back sorted by code, version desc → one row per form code. */
const groupByCode = (forms) => {
  const groups = new Map()
  forms.forEach((f) => {
    if (!groups.has(f.code)) groups.set(f.code, [])
    groups.get(f.code).push(f)
  })
  return [...groups.values()].map((versions) => ({
    latest: versions[0],
    live: versions.find((v) => v.status === 'PUBLISHED'),
    draft: versions.find((v) => v.status === 'DRAFT'),
    count: versions.length,
  }))
}

const FormListPage = () => {
  const navigate = useNavigate()
  const tenantId = useTenantKey()
  const { data, isLoading, error } = useQuery({ queryKey: [tenantId, 'forms', 'all'], queryFn: () => formsAPI.list() })

  if (isLoading) return <Loading />
  if (error) return <Alert severity="error">{getErrorMessage(error)}</Alert>
  const groups = groupByCode(data)

  return (
    <>
      <PageHeader
        title="Forms"
        subtitle="Forms define the fields of each record type. Publish a form to start capturing records with it."
        actions={<Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/forms/new')}>New form</Button>}
      />
      {groups.length === 0 ? (
        <Paper variant="outlined">
          <EmptyState icon={<DynamicFormOutlinedIcon />} title="No forms yet" description="Create a form, for example “Lead”, add fields, and publish it."
            action={<Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/forms/new')}>New form</Button>} />
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Record type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Versions</TableCell>
                <TableCell>Updated</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map(({ latest, live, draft, count }) => (
                <TableRow key={latest.code} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/forms/${(draft || live || latest).id}`)}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{latest.name}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{latest.code}</Typography>
                  </TableCell>
                  <TableCell>{entityLabel(latest.entityType)}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      {live && <Chip label={`v${live.version} live`} color="success" variant="outlined" />}
                      {draft && <Chip label={`v${draft.version} draft`} color="warning" variant="outlined" />}
                      {!live && !draft && <Chip label={latest.status.toLowerCase()} color={STATUS_COLOR[latest.status]} variant="outlined" />}
                    </Stack>
                  </TableCell>
                  <TableCell>{count}</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{formatRelativeTime(latest.updatedOn)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  )
}

export default FormListPage
